import { categoryCounts, topTags } from '../filters';
import { CATEGORY_META, metaFor } from '../data/categories';
import type { AppState, Entry } from '../types';
import { countryName, escapeHtml } from './util';

export interface FilterCallbacks {
  onCategory: (category: string | null) => void;
  onTag: (tag: string) => void;
  onCountryClear: () => void;
}

/** Editorial filter index: categories, contextual tags, country readout. Sorting lives in the table header. */
export function renderFilterIndex(
  container: HTMLElement,
  state: AppState,
  entries: Entry[],
  cb: FilterCallbacks,
): void {
  const counts = categoryCounts(state, entries);
  const total = [...counts.values()].reduce((a, b) => a + b, 0);
  const known = Object.keys(CATEGORY_META).filter((c) => (counts.get(c) ?? 0) > 0);
  const extras = [...counts.keys()].filter((c) => !CATEGORY_META[c]);
  const cats = [...known, ...extras];

  const catItems = [
    `<button class="fi-item ${state.categories.length === 0 ? 'fi-active' : ''}" data-cat="">All <span class="fi-count">${total}</span></button>`,
    ...cats.map(
      (c) =>
        `<button class="fi-item ${state.categories.includes(c) ? 'fi-active' : ''}" data-cat="${escapeHtml(c)}" aria-pressed="${state.categories.includes(c)}">${escapeHtml(metaFor(c).label)} <span class="fi-count">${counts.get(c) ?? 0}</span></button>`,
    ),
  ].join('');

  const tags = topTags(state, entries);
  const tagLine = tags.length
    ? `<div class="fi-line fi-tags" role="group" aria-label="Tags">${tags
        .map(
          (t) =>
            `<button class="fi-tag ${state.tags.includes(t) ? 'fi-active' : ''}" data-tag="${escapeHtml(t)}"><span class="fi-hash">#</span>${escapeHtml(t)}</button>`,
        )
        .join('')}</div>`
    : '';

  const countryLine = state.country
    ? `<div class="fi-line fi-country">Filtering: ${escapeHtml(countryName(state.country))}
        <button class="fi-clear" data-country-clear aria-label="Clear country filter">×</button></div>`
    : '';

  container.innerHTML = `
    <div class="fi-line fi-main">
      <div class="fi-cats" role="group" aria-label="Categories">${catItems}</div>
    </div>
    ${tagLine}
    ${countryLine}`;

  container.querySelectorAll<HTMLButtonElement>('[data-cat]').forEach((btn) => {
    btn.addEventListener('click', () => cb.onCategory(btn.dataset.cat || null));
  });
  container.querySelectorAll<HTMLButtonElement>('[data-tag]').forEach((btn) => {
    btn.addEventListener('click', () => cb.onTag(btn.dataset.tag!));
  });
  container.querySelector('[data-country-clear]')?.addEventListener('click', cb.onCountryClear);
}
